// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import { CenteredLoader } from '../components/CenteredLoader';
import { EditRoleModal, uniformCheckEngineOf } from '../components/EditRoleModal';
import { FeatureMatrixTable, type FeatureMatrixGroup } from '../components/FeatureMatrixTable';
import { NoProjectSelectedState } from '../components/NoProjectSelectedState';
import { useState, useEffect, useCallback } from 'react';
import {
  Loader2,
  AlertCircle,
  Save,
  CheckCircle,
} from 'lucide-react';
import { rolesAPI, type UniformCheckEngine } from '../services/api';
import { useAuthStore } from '../stores/authStore';

// Mobile features grouped by functional area. Multi-item groups render a
// header row + indented children; single-item groups render a single flat row.
// `key` must match the actual feature key in the mobile app.
const MOBILE_FEATURE_GROUPS: { label: string; features: { key: string; label: string }[] }[] = [
  {
    label: 'Attendance Marking',
    features: [
      { key: 'start_end_day', label: 'Start / End Day' },
      { key: 'market_regularization', label: 'Market Regularisation' },
      { key: 'attendance_reminder', label: 'Attendance Reminder Notification' },
    ],
  },
  {
    label: 'Attendance Tracking',
    features: [
      { key: 'attendance', label: 'Attendance (Self)' },
      { key: 'team_attendance', label: 'Attendance (Team)' },
      // OFF by default, and the mobile app treats an ungranted key as hidden, so
      // leaving this unticked is what keeps an employee from reading their own
      // sign-out time and working out the hours behind an overtime claim.
      // Self-view only — it never affects "Attendance (Team)" above, so a manager
      // reviewing a reportee keeps seeing their times either way. Tick it for
      // manager/TL roles if they should also see it on their OWN record.
      { key: 'attendance_work_hours', label: 'Check-out Time & Hours Worked (Self)' },
    ],
  },
  {
    // Random Attendance gets its own header — both the self flag (moved out of
    // Attendance Marking) and the team flag (moved out of Attendance Tracking).
    label: 'Random Attendance',
    features: [
      { key: 'random_attendance', label: 'Random Attendance Self' },
      { key: 'team_random_attendance', label: 'Random Attendance (Team)' },
    ],
  },
  {
    label: 'Activities',
    features: [
      { key: 'tasks', label: 'Tasks' },
      { key: 'emoji', label: 'Emoji (Mood Check)' },
      // No Voice to Text row: the mic in free-text boxes is for everyone, with no tick (owner
      // decision, 23 Oct 2026). The server puts voice_answers in every phone's feature map.
    ],
  },
  {
    label: 'Tasks History',
    features: [
      { key: 'task_history', label: 'Task History (Self)' },
      { key: 'team_task_history', label: 'Task History (Team)' },
    ],
  },
  {
    label: 'Grooming',
    features: [
      { key: 'grooming', label: 'Grooming (Self)' },
      { key: 'team_grooming', label: 'Grooming (Team)' },
    ],
  },
  {
    label: 'Leave',
    features: [
      { key: 'leave_policy', label: 'Leave' },
      { key: 'leave_ledger', label: 'Leave Ledger' },
      { key: 'comp_off', label: 'Comp Off' },
      // Server-enforced policy, not a mobile screen: an hourly scheduler reads this
      // key and files a Week Off request for the store's weekly off day, so ticking
      // it creates real leave records in 1Office. This tick is the switch; the
      // STORE_WEEKOFF_AUTO_APPLY_ENABLED server variable is only an emergency off.
      { key: 'weekoff_auto_apply', label: 'Week Off Auto-apply (creates leave on store weekly off)' },
    ],
  },
  {
    label: 'Our Store Visits',
    features: [
      { key: 'team_store_details', label: 'Team Stores List' },
      { key: 'store_visits', label: 'Store Visits' },
      // The person's own Pre-Journey Plan on Store Visits: the plan card, the Visit Plan
      // screen and the plan line on a store's page. Off until ticked.
      { key: 'visit_plan', label: 'Visit Plan' },
      { key: 'add_store', label: 'Add New Store' },
      // Server-enforced policy, not a UI toggle: with this on, a store added by this role
      // is created immediately instead of waiting in the approval queue.
      { key: 'add_store_auto_approve', label: 'Auto-approve New Stores' },
    ],
  },
  {
    // Red Flags header — mobile red flags have NO dedicated feature key; they live
    // inside the employee/manager "Daily Focus" screens, so these two keys ARE the
    // red-flag self/team gates. Presented as Self/Team to mirror the Yellow Flag
    // group. (employee_focus/manager_focus also drive the broader Daily Focus card;
    // this is a relabel of that same capability, not a new red-flag-only key.)
    label: 'Red Flags',
    features: [
      { key: 'employee_focus', label: 'Red Flag (Self)' },
      { key: 'manager_focus', label: 'Red Flag (Team)' },
    ],
  },
  {
    label: 'HR',
    features: [
      { key: 'approvals', label: 'Approvals' },
      { key: 'document_vault', label: 'Document Vault' },
      // OFF until ticked. Red dots on HR → Document Vault → Personal for employees
      // with no resume on file; they need Document Vault ticked too, and anyone who
      // already has a resume never sees one. The home "Upload resume" task banner
      // is separate — it is sent from Push Personalized Banners → Task activity.
      { key: 'resume_reminder', label: 'Resume Reminder' },
      { key: 'face_match_test', label: 'Face Match Test (Beta)' },
    ],
  },
  {
    label: 'Grow',
    features: [
      { key: 'grow_content_modules', label: 'Content Modules' },
      { key: 'grow_sessions', label: 'Training Sessions' },
      { key: 'grow_journeys', label: 'Learning Journeys' },
      // Belongs here, not under Employee Score Card: grow_leaderboard gates the
      // Grow tab's LMS *points* leaderboard (GrowScreen → LmsLeaderboard), which
      // ranks learning points and is unrelated to the scorecard. Listing it under
      // Employee Score Card implied it toggled that screen's podium standings —
      // it never did; the podium is part of the Employee Score Card screen itself.
      { key: 'grow_leaderboard', label: 'Leaderboard' },
      // A manager's view of their team's learning: the Team side of the Grow tab, read only.
      { key: 'grow_team', label: 'Grow (Team)' },
    ],
  },
  {
    label: 'Training',
    features: [
      { key: 'training', label: 'Training (Self)' },
      { key: 'team_training', label: 'Training (Team)' },
      // For the employee HOSTING a new joiner, not the coordinator who assigns
      // them — that is the separate web.onboarding grant.
      { key: 'shadow_training_buddy', label: 'Shadow Training (Buddy)' },
      // The NEW JOINER's own onboarding card + 14-day journey timeline.
      { key: 'onboarding_journey', label: 'Onboarding Journey (Joiner)' },
      // A manager's view of their team's onboarding.
      { key: 'onboarding_team', label: 'Onboarding (Team)' },
    ],
  },
  {
    // One Scorecard header, two audiences. Each key turns on a whole screen —
    // the score AND its podium Leaderboard / Breakdown / 6-Month tabs, which are
    // part of that same screen and have no separate keys of their own.
    //
    //   Employee Scorecard — "how did I do at my store", scored on the employee's
    //     own metrics, ranked against everyone under their direct manager.
    //   Manager Scorecard  — "how is my team doing", scored on the Team Average
    //     Score rollup configured for their role, ranked against every manager in
    //     that role project-wide (not just peers under the same upper manager).
    //
    // A manager who should see both gets both ticked; they are independent.
    label: 'Scorecard',
    features: [
      { key: 'employee_score', label: 'Employee Scorecard' },
      { key: 'manager_score', label: 'Manager Scorecard' },
    ],
  },
  {
    // Sales & Targets on the phone. Three separate ticks because they are three
    // separate decisions:
    //   sales_targets      — a promoter sees their own targets, sales and pace.
    //   sales_incentive    — ...and the incentive they are on course for, with how
    //                        many more units reach the next slab. Pay figures, so it
    //                        is its own tick; the server also refuses it without one.
    //   team_sales_targets — a manager sees how their reportees stand, product by
    //                        product (no money), and each employee's numbers on the
    //                        Store Visit screen.
    label: 'Sales & Targets',
    features: [
      { key: 'sales_targets', label: 'Sales & Targets (Self)' },
      { key: 'sales_incentive', label: 'Incentive Estimate (Self)' },
      { key: 'team_sales_targets', label: 'Sales & Targets (Team)' },
    ],
  },
  {
    label: 'Yellow Flag',
    features: [
      { key: 'yellow_flag', label: 'Yellow Flag (Self)' },
      { key: 'team_yellow_flag', label: 'Yellow Flag (Team)' },
    ],
  },
  {
    // Single flag for the whole PIP feature — the employee's own PIP card + the
    // manager's Team-on-PIP card (enable per role for whoever should see it).
    label: 'Performance Improvement Plan',
    features: [
      { key: 'pip', label: 'Performance Improvement Plan' },
    ],
  },
  {
    // Role-based quarterly KPI — the manager's own KPI tile + drill-down.
    // Independent of the two scorecard keys; enable per role for whoever the KPI
    // is configured for.
    label: 'KPI',
    features: [
      { key: 'kpi', label: 'KPI' },
    ],
  },
  {
    // Team attrition rate on the manager's home screen, for the last completed
    // month. Scoped to the manager's reporting subtree, so it reports a different
    // population than the Attrition dashboard tab and will not match it.
    label: 'Attrition',
    features: [
      { key: 'team_attrition', label: 'Team Attrition (Manager)' },
    ],
  },
  {
    // Social Connect — the in-app employee feed and its chats. The first four are
    // INDEPENDENT gates on the feed, so a role can be read-only, or allowed to post
    // but not to pin:
    //   social_feed           — master switch: see the feed at all. Without it every
    //                           feed endpoint 403s, so the rest are inert.
    //   social_post_create    — BROADCAST: new posts and reposts.
    //   social_comment_create — REPLY: comments on someone else's post. Split out from
    //                           social_post_create so "read and reply, but don't start
    //                           a thread" is expressible. Existing roles were backfilled
    //                           at boot, so unticking one is the ONLY way they diverge.
    //                           A LIKE needs either of the two — see togglePostLike.
    //   social_announcement   — publish a PINNED announcement, which is prepended to
    //                           page 1 of BOTH feed scopes. Managers / HR only.
    // The last three are the Chats part of Connect. None of them needs a feed tick:
    //   social_dm             — one-to-one messages with anyone in the same project.
    //   social_group          — take part in group chats: see the groups you are in,
    //                           read, send and get the push. OFF for every role until
    //                           ticked. It does not need Direct Messages. It also
    //                           decides who can be ADDED to a group: the server refuses
    //                           a person whose role lacks it.
    //   social_group_create   — start a group. OFF until ticked, and it does nothing on
    //                           its own: the role needs Group Chats ticked too. This
    //                           page does not tick one for the other; the server asks
    //                           for both. Renaming a group and adding or removing
    //                           people go with being that group's admin, not with
    //                           this tick.
    label: 'Social Connect',
    features: [
      { key: 'social_feed', label: 'View Feed' },
      { key: 'social_post_create', label: 'Create Posts' },
      { key: 'social_comment_create', label: 'Comment on Posts' },
      { key: 'social_announcement', label: 'Post Announcements (Pinned)' },
      // These two also switch on the web inbox (Messages, in the top bar): the same
      // conversations, read in a browser. The labels say so, because this tab is
      // otherwise about the phone.
      { key: 'social_dm', label: 'Direct Messages (app and web)' },
      { key: 'social_group', label: 'Group Chats (app and web)' },
      { key: 'social_group_create', label: 'Create Groups (needs Group Chats)' },
    ],
  },
  {
    // OFF until ticked. With this tick the app's floating "Questions or Concerns?"
    // button opens the Helpdesk chat inside the app; without it the button opens
    // WhatsApp, exactly as before. The tick is the whole switch on our side, for any
    // project; the helpdesk team's own config decides which projects their bot serves
    // (anyone else is told "not available for your account"). While the helpdesk bot is
    // not answering, the server keeps WhatsApp in place by itself. A one-row group shows
    // the group's label, so the wording lives there.
    label: 'Helpdesk Chat (In-app)',
    features: [
      { key: 'helpdesk_chat', label: 'Helpdesk Chat (In-app)' },
    ],
  },
];

// Keys some roles still hold from a tick this page no longer has. 'voice_answers' was the Voice to
// Text tick (#782) until the mic became for everyone (23 Oct 2026); the server now puts it in every
// phone's map whatever the role holds. Left in the saved list (harmless), but not counted.
const RETIRED_MOBILE_FEATURE_KEYS: ReadonlySet<string> = new Set(['voice_answers']);

// Normalized into the shared <FeatureMatrixTable> group shape. A single-feature
// group becomes a singleton (one flat row labelled with the group label); a
// multi-feature group becomes a header (group label) + indented child rows.
const MOBILE_FEATURE_MATRIX_GROUPS: FeatureMatrixGroup[] = MOBILE_FEATURE_GROUPS.map((group) => ({
  key: group.label,
  label: group.label,
  rows: group.features.map((feature) => ({ key: feature.key, label: feature.label })),
}));

interface Role {
  id: string;
  name: string;
  description?: string;
  category?: string;
  geo_based_attendance?: boolean;
  aws_face_verification?: boolean;
  uniform_check_engine?: UniformCheckEngine;
  multi_store_deployment?: boolean;
  user_count?: number;
}

export default function MobileAccessTab({ refreshKey }: { refreshKey?: number }) {
  const { selectedProjectId } = useAuthStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [editingRole, setEditingRole] = useState<{ id: string; name: string; description: string; geoBased: boolean; awsVerification: boolean; uniformCheckEngine: UniformCheckEngine; multiStore: boolean } | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // Feature toggles per role: { [roleId]: string[] of feature_keys }
  const [featureChanges, setFeatureChanges] = useState<Record<string, string[]>>({});

  // Fetch roles and feature mappings
  const fetchData = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      setLoading(true);
      setError(null);

      const rolesRes = await rolesAPI.getByProject(selectedProjectId);
      const fetchedRoles: Role[] = rolesRes.data?.data || [];
      setRoles(fetchedRoles);

      // Initialize feature changes from mappings
      const changes: Record<string, string[]> = {};
      fetchedRoles.forEach((role) => { changes[role.id] = []; });

      // Fetch feature mappings separately so roles still load if table doesn't exist
      try {
        const mappingsRes = await rolesAPI.getAllFeatureMappings();
        if (mappingsRes.data?.data) {
          mappingsRes.data.data.forEach((m: { role_id: string; feature_key: string }) => {
            if (changes[m.role_id]) {
              changes[m.role_id].push(m.feature_key);
            }
          });
        }
      } catch {
        // Table may not exist yet — proceed with empty mappings
      }
      setFeatureChanges(changes);
      setHasChanges(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // Check if a role has a specific feature
  const hasFeature = (roleId: string, featureKey: string): boolean => {
    return featureChanges[roleId]?.includes(featureKey) || false;
  };

  // Toggle feature for a role
  const toggleFeature = (roleId: string, featureKey: string) => {
    setFeatureChanges((prev) => {
      const current = prev[roleId] || [];
      const updated = current.includes(featureKey)
        ? current.filter((k) => k !== featureKey)
        : [...current, featureKey];
      return { ...prev, [roleId]: updated };
    });
    setHasChanges(true);
  };

  // Get feature count for a role
  const openEditRole = (role: Role) => {
    setEditingRole({
      id: role.id,
      name: role.name,
      description: role.description || '',
      geoBased: role.geo_based_attendance || false,
      awsVerification: role.aws_face_verification || false,
      uniformCheckEngine: uniformCheckEngineOf(role.uniform_check_engine),
      multiStore: role.multi_store_deployment || false,
    });
  };

  const handleEditRole = async () => {
    if (!editingRole) return;
    setSavingEdit(true);
    try {
      await rolesAPI.update(editingRole.id, {
        name: editingRole.name,
        description: editingRole.description,
        geo_based_attendance: editingRole.geoBased,
        aws_face_verification: editingRole.awsVerification,
        // Hidden while the toggle is off, and then left as saved.
        ...(editingRole.awsVerification ? { uniform_check_engine: editingRole.uniformCheckEngine } : {}),
        multi_store_deployment: editingRole.multiStore,
      });
      setEditingRole(null);
      fetchData();
    } catch (err: any) {
      console.error('Error updating role:', err);
      alert(err.response?.data?.message || 'Failed to update role');
    } finally {
      setSavingEdit(false);
    }
  };

  // The role header's "N features". A key that is no longer a tick (RETIRED_MOBILE_FEATURE_KEYS)
  // is not counted, though Save keeps it like any other key the page does not show.
  const getFeatureCount = (roleId: string): number => {
    return featureChanges[roleId]?.filter((featureKey) => !RETIRED_MOBILE_FEATURE_KEYS.has(featureKey)).length || 0;
  };

  // Save all changes
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await Promise.all(
        roles.map((role) =>
          rolesAPI.updateRoleFeatures(role.id, featureChanges[role.id] || [])
        )
      );
      setHasChanges(false);
      setSuccessMsg('Mobile feature access saved successfully');
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save feature access');
    } finally {
      setSaving(false);
    }
  };

  if (!selectedProjectId) {
    return (
      <NoProjectSelectedState message="Select a project from the dropdown in the menu bar." />
    );
  }

  if (loading) {
    return <CenteredLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Banners */}
      {successMsg && (
        <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          {successMsg}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {/* Info banner when no roles exist */}
      {roles.length === 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          No roles found for this project. Create a role using the "Add Role" button above to assign mobile feature access.
        </div>
      )}

      {/* Save bar */}
      {roles.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Toggle mobile feature access for each role. Changes apply when you save.
          </p>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}

      {/* Matrix Table — always show features */}
      <FeatureMatrixTable
        headerLabel="Mobile Features"
        headerColumnMinWidthClassName="min-w-[220px]"
        roles={roles}
        groups={MOBILE_FEATURE_MATRIX_GROUPS}
        isChecked={(roleId, featureKey) => hasFeature(roleId, featureKey)}
        onToggle={(roleId, featureKey) => toggleFeature(roleId, featureKey)}
        onEditRole={(role) => openEditRole(role)}
        countLabel={(role) => <>{getFeatureCount(role.id)} features</>}
      />
      {/* Edit Role Modal */}
      {editingRole && (
        <EditRoleModal
          key={editingRole.id}
          value={editingRole}
          onChange={setEditingRole}
          onClose={() => setEditingRole(null)}
          onSave={handleEditRole}
          saving={savingEdit}
          projectId={selectedProjectId}
        />
      )}
    </div>
  );
}
